import { z } from "zod";

import type {
	CreateHotelPromoBody,
	UpdateHotelPromoBody,
} from "@/features/hotel-promos/types";
import i18n from "@/i18n";

const CODE_PATTERN = /^[A-Za-z0-9._-]+$/;

type HotelPromoGuest = {
	id: string;
	label: string;
};

export type HotelPromoFormValues = {
	code: string;
	percent: number;
	isActive: boolean;
	partnerIds: string[];
	categoryIds: string[];
	guests: HotelPromoGuest[];
	hotelKeys: string[];
};

export function emptyHotelPromoValues(): HotelPromoFormValues {
	return {
		code: "",
		percent: 10,
		isActive: true,
		partnerIds: [],
		categoryIds: [],
		guests: [],
		hotelKeys: [],
	};
}

export function hotelPromoSchema(mode: "create" | "edit") {
	return z
		.object({
			code: z
				.string()
				.trim()
				.min(1, i18n.t("hotel.codeRequired"))
				.max(50, i18n.t("hotel.codeInvalid"))
				.regex(CODE_PATTERN, i18n.t("hotel.codeInvalid")),
			percent: z
				.number({
					required_error: i18n.t("hotel.percentRequired"),
					invalid_type_error: i18n.t("hotel.percentRequired"),
				})
				.int()
				.min(1, i18n.t("hotel.percentRequired"))
				.max(100, i18n.t("hotel.percentRequired")),
			isActive: z.boolean(),
			partnerIds: z.array(z.string()),
			categoryIds: z.array(z.string()),
			guests: z.array(z.object({ id: z.string(), label: z.string() })),
			hotelKeys: z.array(z.string()).min(1, i18n.t("hotel.hotelsRequired")),
		})
		.superRefine((values, ctx) => {
			if (mode === "edit") {
				return;
			}
			if (
				!values.categoryIds.length &&
				!values.guests.length &&
				!values.partnerIds.length
			) {
				ctx.addIssue({
					code: "custom",
					path: ["categoryIds"],
					message: i18n.t("hotel.targetsRequired"),
				});
			}
		});
}

export function createHotelPromoBody(
	values: HotelPromoFormValues,
): CreateHotelPromoBody {
	return {
		code: values.code.trim(),
		percent: values.percent,
		isActive: values.isActive,
		partnerIds: values.partnerIds,
		categoryIds: values.categoryIds,
		userIds: values.guests.map((guest) => guest.id),
		hotelKeys: values.hotelKeys,
	};
}

export function updateHotelPromoBody(
	values: HotelPromoFormValues,
): UpdateHotelPromoBody {
	return {
		code: values.code.trim(),
		percent: values.percent,
		isActive: values.isActive,
		hotelKeys: values.hotelKeys,
		partnerIds: values.partnerIds,
		categoryIds: values.categoryIds,
		userIds: values.guests.map((guest) => guest.id),
	};
}
